import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';

import { Button } from '../../shared/ui/button';
import { FieldLabel } from '../../shared/ui/field-label';
import { Hint } from '../../shared/ui/hint';
import { PlusIcon } from '../../shared/ui/icons';
import { Input } from '../../shared/ui/input';
import { Modal } from '../../shared/ui/modal';
import { Note } from '../../shared/ui/note';
import { Segmented } from '../../shared/ui/segmented';
import {
  addSourceCustomer,
  liveSecrets,
  sourceClientKeys,
  upsertCustomerSecret,
  type TSourceCustomer,
} from '../../entities/customer';
import { credentialedMethods, sourceAuthMethods, type TSource } from '../../entities/source';
import { MethodField, SecretShownOnce } from '../../features/source-secrets';

type TSecretChoice = 'generate' | 'paste' | 'later';

const CHOICES = [
  { value: 'generate', label: 'Generate one' },
  { value: 'paste', label: 'Paste theirs' },
  { value: 'later', label: 'Later' },
] as const;

interface IAdded {
  row: TSourceCustomer;
  plaintext: string | null;
  secretError: string | null;
  /** The reference was already someone's: they came back instead of a new client. */
  existing: string | null;
  kept: boolean;
}

interface IAddClientProps {
  source: TSource;
  onAdded: (client: TSourceCustomer) => void;
  variant?: 'primary' | 'secondary';
}

export const AddClient = ({ source, onAdded, variant = 'primary' }: IAddClientProps) => {
  const client = useQueryClient();
  const [open, setOpen] = useState(false);
  const [name, setName] = useState('');
  const [reference, setReference] = useState('');
  const [choice, setChoice] = useState<TSecretChoice>('generate');
  const [secret, setSecret] = useState('');
  const [added, setAdded] = useState<IAdded | null>(null);
  // No method that takes a secret (none at all, or an IP allowlist alone): the server would refuse one.
  const checks = source.clientSecrets;
  const methods = useQuery({
    queryKey: ['source-auth-methods', source.id],
    queryFn: () => sourceAuthMethods(source.id),
    enabled: open && checks,
  });
  const credentialed = credentialedMethods(methods.data ?? []);
  const [picked, setPicked] = useState<string | null>(null);
  const methodId = picked ?? credentialed[0]?.id;

  const reset = () => {
    setName('');
    setReference('');
    setSecret('');
    setChoice('generate');
    setPicked(null);
  };

  // Two calls when generating: the add endpoint only stores a secret it is given. A failed second
  // call leaves a client without one — said here, and fixable on their page.
  const add = useMutation({
    mutationFn: async (): Promise<IAdded> => {
      const row = await addSourceCustomer(source.id, {
        name: name.trim(),
        ...(reference.trim() ? { externalId: reference.trim() } : {}),
        ...(checks && choice === 'paste' && secret.trim()
          ? { secret: secret.trim(), ...(methodId ? { authMethodId: methodId } : {}) }
          : {}),
      });
      const live = liveSecrets(row.secrets);
      const hadSecret = choice !== 'paste' && live.current.length + live.old.length > 0;
      const existing = row.customer.name !== name.trim() || hadSecret ? row.customer.name : null;
      // A taken reference returns that client; generating here would silently rotate their secret.
      if (!checks || choice !== 'generate' || hadSecret) {
        return { row, plaintext: null, secretError: null, existing, kept: hadSecret };
      }
      try {
        const made = await upsertCustomerSecret(
          row.customer.id,
          source.id,
          methodId ? { authMethodId: methodId } : {},
        );
        return { row, plaintext: made.secret.plaintext, secretError: null, existing, kept: false };
      } catch (error) {
        const secretError = error instanceof Error ? error.message : '';
        return { row, plaintext: null, secretError, existing, kept: false };
      }
    },
    onSuccess: (result) => {
      void client.invalidateQueries({ queryKey: sourceClientKeys.all(source.id) });
      void client.invalidateQueries({ queryKey: ['source', source.id] });
      setOpen(false);
      reset();
      if (result.plaintext === null && result.secretError === null && result.existing === null) {
        onAdded(result.row);
      } else setAdded(result);
    },
  });

  const existingNote = added?.existing ? (
    <Note tone="caution">
      The reference {added.row.customer.externalId} is already {added.existing}’s, so no new client
      was made — this is {added.existing}, now on {source.name}.
      {added.kept ? ' They already have a working secret here, so it was left as it was.' : ''}
    </Note>
  ) : null;

  const finish = () => {
    if (added) onAdded(added.row);
    setAdded(null);
  };

  return (
    <>
      <Button size="sm" variant={variant} onClick={() => setOpen(true)}>
        <PlusIcon size={12} /> Add a client
      </Button>

      <Modal
        open={open}
        onOpenChange={(next) => {
          setOpen(next);
          if (!next) {
            reset();
            add.reset();
          }
        }}
        title="Add a client"
        description="They get an address of their own the moment you create them."
        footerNote="opens the new client’s page"
        submit={{
          label: 'Create',
          onSubmit: () => add.mutate(),
          disabled:
            !name.trim() ||
            (checks && choice !== 'later' && methods.isPending) ||
            (checks && choice === 'paste' && !secret.trim()),
          waiting: add.isPending,
          waitingLabel: 'Creating',
        }}
      >
        <div className="flex flex-col gap-4">
          <div>
            <FieldLabel htmlFor="add-client-name">Name</FieldLabel>
            <Input
              id="add-client-name"
              autoFocus
              placeholder="Their company name"
              value={name}
              onChange={(event) => setName(event.target.value)}
            />
          </div>
          <div>
            <FieldLabel htmlFor="add-client-reference">Your reference</FieldLabel>
            <Input
              id="add-client-reference"
              mono
              placeholder="optional"
              value={reference}
              onChange={(event) => setReference(event.target.value)}
            />
          </div>
          {checks ? (
            <div>
              <FieldLabel>Their secret</FieldLabel>
              <Segmented
                fill
                aria-label="Their secret"
                segments={CHOICES}
                value={choice}
                onChange={setChoice}
              />
              {choice === 'paste' && (
                <Input
                  mono
                  className="mt-2"
                  aria-label="The secret they sign with"
                  placeholder="the secret they sign with"
                  value={secret}
                  onChange={(event) => setSecret(event.target.value)}
                />
              )}
              <Hint>
                {choice === 'later'
                  ? source.onAuthFailure === 'reject'
                    ? 'Later: their address works only once a secret is added. Until then everything sent to it is rejected.'
                    : `Later: until a secret is added, nothing sent to their address can be checked, so ${source.name} delivers it marked as failed.`
                  : choice === 'generate'
                    ? 'Made when you press Create and shown once, for you to send them.'
                    : 'As they gave it to you.'}
              </Hint>
            </div>
          ) : (
            <p className="font-mono text-micro text-faint">
              {source.unauthenticated
                ? 'Secrets come once the source checks signatures.'
                : `No secret: ${source.name} checks only the IP address requests come from.`}
            </p>
          )}
          {checks && choice !== 'later' && methodId && (
            <MethodField
              id="add-client-method"
              methods={credentialed}
              value={methodId}
              onChange={setPicked}
            />
          )}
        </div>
        {add.isError && (
          <Note tone="danger" className="mt-3">
            Not created: {add.error.message}
          </Note>
        )}
      </Modal>

      <SecretShownOnce
        plaintext={added?.plaintext ?? null}
        clientName={added?.row.customer.name ?? ''}
        doneLabel="Open their page"
        onClose={finish}
      >
        {existingNote}
      </SecretShownOnce>

      <Modal
        open={added !== null && added.plaintext === null}
        onOpenChange={(next) => !next && finish()}
        title={
          added?.existing
            ? `${added.existing} is already a client`
            : `${added?.row.customer.name ?? ''} was created`
        }
        footer={
          <Button size="sm" onClick={finish}>
            Open their page
          </Button>
        }
      >
        <div className="flex flex-col gap-3">
          {existingNote}
          {added?.secretError != null && (
            <Note tone="danger">
              No secret was generated: {added.secretError} Add one on their page.
            </Note>
          )}
        </div>
      </Modal>
    </>
  );
};
